import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AddBankModal,
  AddCardModal,
  PaymentMethodPicker,
} from "../../../components/payment";
import { usePaymentMethods } from "../../../hooks/usePaymentMethods";
import { useRenewal } from "../RenewalContext";
import { BILLING_INFO } from "../renewalCopy";
import { Card, Notice, StepHeading, StepNav } from "../renewalUi";

interface Props {
  onBack: () => void;
  onNext: () => void;
}

/**
 * Confirm the payment method that will be billed at renewal. Reuses the same
 * PaymentMethodPicker as the SDA purchase and claim-repayment screens, so
 * rows and keyboard behavior stay identical across the app.
 *
 * Adding a method happens inline via the same AddBank/AddCard modals the
 * SDA flow and /payment-methods use — sending the tenant off to another page
 * mid-flow would lose their progress (the draft is in-memory).
 *
 * Nothing is charged here — this step only confirms which method is on file.
 * A tenant whose term is already confirmed can pay immediately from the
 * post-submit confirmation screen instead (see RenewFlow).
 */
const PaymentStep: React.FC<Props> = ({ onBack, onNext }) => {
  const { t } = useTranslation();
  const { draft, update } = useRenewal();
  const {
    methods,
    loading,
    refresh: refreshMethods,
  } = usePaymentMethods();

  const [showAddCard, setShowAddCard] = useState(false);
  const [showAddBank, setShowAddBank] = useState(false);

  // Default to the tenant's default method (or the first one) on arrival.
  useEffect(() => {
    if (draft.paymentMethodId !== null || methods.length === 0) return;
    const def = methods.find((m) => m.isDefault) ?? methods[0];
    if (def) update({ paymentMethodId: def.id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [methods, draft.paymentMethodId]);

  const addButtons = (
    <div className="flex gap-2">
      <button
        type="button"
        onClick={() => setShowAddBank(true)}
        className="cursor-pointer rounded-lg border border-primary px-4 py-2 text-sm font-medium text-primary transition-colors hover:bg-primary/5"
      >
        {t("paymentMethods.addBank")}
      </button>
      <button
        type="button"
        onClick={() => setShowAddCard(true)}
        className="cursor-pointer rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-light"
      >
        {t("paymentMethods.addCard")}
      </button>
    </div>
  );

  return (
    <>
      <StepHeading
        title="Confirm your payment info"
        subtitle="This is the method we'll bill for your next lease term."
      />

      <Card className="mb-4">
        {loading ? (
          <p className="text-sm text-muted">Loading payment methods...</p>
        ) : methods.length === 0 ? (
          <div className="rounded-lg border-2 border-dashed border-gray-200 p-5 text-center">
            <p className="mb-3 text-sm text-gray-700">
              You don't have a payment method on file yet.
            </p>
            <div className="flex justify-center">{addButtons}</div>
          </div>
        ) : (
          <>
            <div className="mb-3 flex items-center justify-between gap-3">
              <p className="text-sm font-semibold text-primary">
                Select a payment method
              </p>
              {addButtons}
            </div>
            <PaymentMethodPicker
              methods={methods}
              selectedId={draft.paymentMethodId}
              onSelect={(id) => update({ paymentMethodId: id })}
            />
          </>
        )}
      </Card>

      <Notice tone="info" title={BILLING_INFO.title}>
        {BILLING_INFO.body}
      </Notice>

      <StepNav
        onBack={onBack}
        onNext={onNext}
        nextDisabled={draft.paymentMethodId === null}
      />

      <AddCardModal
        open={showAddCard}
        onClose={() => setShowAddCard(false)}
        onSaved={() => {
          setShowAddCard(false);
          refreshMethods();
        }}
      />

      <AddBankModal
        open={showAddBank}
        onClose={() => setShowAddBank(false)}
        onConnected={() => {
          setShowAddBank(false);
          refreshMethods();
        }}
      />
    </>
  );
};

export default PaymentStep;
